import { isEmpty } from 'lodash'

import constants from 'app/constants'

import {
  apiEndpoints,
  validateDeleteRequest,
  validateUpdateRequest
} from './common'

import { makeRequestUrl, showToast } from '../app/helpers'
import { get, patch, post, remove } from '../request'

/**
 * The centres the logged-in user may work with — NOT every centre in the company.
 *
 * Not to be confused with `fetchMyCentres` in api/procurement.js, which despite
 * the name returns EVERY centre (limit=1000) — procurement has no centre role
 * yet. That one is imported directly from 'api/procurement'; this one is the
 * barrel export.
 *
 * Centre Operations pages must use this rather than fetchCentre(): somebody mapped
 * to a centre in HQ -> Roles & Permissions -> People only ever gets their own back,
 * and the APIs behind those pages scope their data the same way, so a picker built
 * from the full list would just offer choices that come back empty.
 *
 * The list is returned under BOTH `results` and `centres` so the existing callers'
 * extractors keep working unchanged. `unrestricted` is false for a centre-mapped
 * user — pages use it to decide whether an "all centres" option means anything.
 */
const fetchScopedCentres = async () => {
  try {
    const response = await get(apiEndpoints.operationsMyCentres)
    const centres = response.data?.centres || []
    return {
      results: centres,
      centres,
      unrestricted: Boolean(response.data?.unrestricted)
    }
  } catch (error) {
    console.log('error in fetching my centres :- ', error.message)
    throw error
  }
}

const centreRequestStructure = (data) => ({
  name: data.name,
  postal_address: data.postal_address,
  postal_code: data.postal_code,
  city: data.city,
  gmap_link: data.gmap_link,
  phone_number: data.phone_number
})

const fetchCentre = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.centre, queries)

  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    // showToast('Oops, Error in fetching centre', constants.ERROR)
    console.log('error in fetching centre :- ', error.message)
    throw error
  }
}

const createCentre = async (data) => {
  try {
    const response = await post(
      apiEndpoints.centre,
      centreRequestStructure(data)
    )

    return response.data
  } catch (error) {
    console.log('error in creating centre :- ', error.message)
    throw error
  }
}

const updateCentre = async (data) => {
  validateUpdateRequest(data)

  try {
    const response = await patch(
      `${apiEndpoints.centre}/${data.uuid}/`,
      centreRequestStructure(data)
    )

    return response.data
  } catch (error) {
    console.log('error in updating centre :- ', error.message)
    throw error
  }
}

const deleteCentre = async ({ ids }) => {
  validateDeleteRequest(ids)

  try {
    await Promise.all(
      ids.map((id) => {
        return remove(`${apiEndpoints.centre}/${id}`)
      })
    )

    return ids
  } catch (error) {
    console.log('error in deleting centre :- ', error.message)
    throw error
  }
}

export {
  fetchScopedCentres,
  fetchCentre,
  createCentre,
  updateCentre,
  deleteCentre
}
